import { WfPropertyType } from 'wflow-main-app/src/app/shared/dumb-components/property-list/property-list.component';
import { formatAddress } from 'wflow-main-app/src/app/shared/utils/format-address.util';
import { Partner } from 'wflow-main-app/src/app/core/api/model/partner';
import { PeppolDetail } from '../interfaces/peppol-detail.interface';
import { PeppolUblDocumentType } from '../interfaces/peppol-item.dto';
import { PeppolUblPreview } from '../interfaces/peppol-ubl-preview.interface';

export interface PeppolUblFieldContext {
  preview: PeppolUblPreview;
  detail: PeppolDetail;
}

export interface PeppolUblFieldDescriptor {
  key: string;
  accessor: (context: PeppolUblFieldContext) => unknown;
  type?: WfPropertyType;
}

export interface PeppolUblPartyContext {
  party: Partner | undefined;
  peppolId: string;
}

export interface PeppolUblPartyFieldDescriptor {
  key: string;
  accessor: (context: PeppolUblPartyContext) => unknown;
  noLabel?: boolean;
}

export interface PeppolUblFieldSet {
  showParties: boolean;
  partyFields: PeppolUblPartyFieldDescriptor[];
  paymentInfo: PeppolUblFieldDescriptor[];
  dates: PeppolUblFieldDescriptor[];
  showLines: boolean;
  showVatRecap: boolean;
  showTotal: boolean;
}

const PARTY_FIELDS: PeppolUblPartyFieldDescriptor[] = [
  {
    key: 'PEPPOL.Preview.PartyAddress',
    accessor: ({ party }) => formatAddress(party?.mainAddress, party?.address),
    noLabel: true,
  },
  {
    key: 'PEPPOL.Preview.PartyIC',
    accessor: ({ party }) => party?.ic,
  },
  {
    key: 'PEPPOL.Preview.PartyVAT',
    accessor: ({ party }) => party?.vat,
  },
  {
    key: 'PEPPOL.Preview.PartyPeppolId',
    accessor: ({ peppolId }) => peppolId,
  },
];

const PAYMENT_FIELDS: PeppolUblFieldDescriptor[] = [
  {
    key: 'PEPPOL.Preview.PaymentMethod',
    accessor: ({ preview }) => preview.paymentMethod,
  },
  {
    key: 'PEPPOL.Preview.AccountNo',
    accessor: ({ preview }) =>
      preview.accountNo && preview.bankCode
        ? `${preview.accountNo}/${preview.bankCode}`
        : preview.accountNo,
  },
  {
    key: 'PEPPOL.Preview.Iban',
    accessor: ({ preview }) => preview.iban,
  },
  {
    key: 'PEPPOL.Preview.Swift',
    accessor: ({ preview }) => preview.bic,
  },
];

const INVOICE_DATE_FIELDS: PeppolUblFieldDescriptor[] = [
  {
    key: 'PEPPOL.Preview.IssueDate',
    accessor: ({ preview }) => preview.issueDate,
    type: 'date',
  },
  {
    key: 'PEPPOL.Preview.DueDate',
    accessor: ({ preview }) => preview.dueDate,
    type: 'date',
  },
  {
    key: 'PEPPOL.Preview.VariableSymbol',
    accessor: ({ preview }) => preview.variableSymbol,
  },
  {
    key: 'PEPPOL.Preview.ConstantSymbol',
    accessor: ({ preview }) => preview.constantSymbol,
  },
];

const ORDER_DATE_FIELDS: PeppolUblFieldDescriptor[] = [
  {
    key: 'PEPPOL.Preview.IssueDate',
    accessor: ({ preview }) => preview.issueDate,
    type: 'date',
  },
];

const INVOICE_FIELD_SET: PeppolUblFieldSet = {
  showParties: true,
  partyFields: PARTY_FIELDS,
  paymentInfo: PAYMENT_FIELDS,
  dates: INVOICE_DATE_FIELDS,
  showLines: true,
  showVatRecap: true,
  showTotal: true,
};

const ORDER_FIELD_SET: PeppolUblFieldSet = {
  showParties: true,
  partyFields: PARTY_FIELDS,
  paymentInfo: [],
  dates: ORDER_DATE_FIELDS,
  showLines: true,
  showVatRecap: false,
  showTotal: false,
};

const PEPPOL_UBL_FIELD_SETS: Record<string, PeppolUblFieldSet> = {
  [PeppolUblDocumentType.Invoice]: INVOICE_FIELD_SET,
  [PeppolUblDocumentType.CreditNote]: INVOICE_FIELD_SET,
  [PeppolUblDocumentType.DebitNote]: INVOICE_FIELD_SET,
  [PeppolUblDocumentType.Order]: ORDER_FIELD_SET,
};

export function findPeppolUblFieldSet(
  documentType: PeppolUblDocumentType | string | undefined,
): PeppolUblFieldSet | undefined {
  return documentType ? PEPPOL_UBL_FIELD_SETS[documentType] : undefined;
}
